import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { User, Shield, Key, Mail, Phone, Calendar, CheckCircle, Save, AlertCircle, Eye, EyeOff, ShieldCheck, Activity, Smartphone, QrCode, Copy, ShieldOff, X } from 'lucide-react';
import profileService, { UserProfile, ProfileUpdateDto, ChangePasswordDto, Activity as ActivityType } from '../services/profileService';
import authService, { TwoFactorSetupResponse } from '../services/authService';

const Profile = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'info' | 'security' | 'activity'>('info');
  const [showPassword, setShowPassword] = useState(false);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [activities, setActivities] = useState<ActivityType[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [profileForm, setProfileForm] = useState<ProfileUpdateDto>({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });

  const [passwordForm, setPasswordForm] = useState<ChangePasswordDto>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // 2FA state
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [twoFactorLoading, setTwoFactorLoading] = useState(false);
  const [showSetup2FA, setShowSetup2FA] = useState(false);
  const [setupData, setSetupData] = useState<TwoFactorSetupResponse | null>(null);
  const [setupCode, setSetupCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [showDisable2FA, setShowDisable2FA] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadProfile();
    loadActivities();
    load2FAStatus();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const data = await profileService.getProfile();
      setProfile(data);
      setProfileForm({
        firstName: data.firstName || '',
        lastName: data.lastName || '',
        email: data.email || '',
        phone: data.phone || ''
      });
    } catch (error) {
      console.error('Profil yuklenirken hata:', error);
      showMessage('error', t('profile.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const loadActivities = async () => {
    try {
      const data = await profileService.getActivities(10);
      setActivities(data);
    } catch (error) {
      console.error('Aktiviteler yuklenirken hata:', error);
    }
  };

  const load2FAStatus = async () => {
    const enabled = await authService.checkTwoFactorStatus();
    setTwoFactorEnabled(enabled);
  };

  const handleStart2FASetup = async () => {
    try {
      setTwoFactorLoading(true);
      setMessage(null);
      const data = await authService.setup2FA();
      setSetupData(data);
      setShowSetup2FA(true);
      setSetupCode('');
    } catch (error: any) {
      showMessage('error', error.response?.data?.message || 'Failed to start 2FA setup');
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const handleEnable2FA = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!setupCode || setupCode.length !== 6) return;

    try {
      setTwoFactorLoading(true);
      await authService.enable2FA(setupCode);
      setTwoFactorEnabled(true);
      setShowSetup2FA(false);
      setSetupData(null);
      setSetupCode('');
      showMessage('success', t('auth.twoFactorEnabled'));
    } catch (error: any) {
      showMessage('error', error.response?.data?.message || t('auth.twoFactorInvalid'));
      setSetupCode('');
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const handleDisable2FA = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!disableCode || disableCode.length !== 6) return;

    try {
      setTwoFactorLoading(true);
      await authService.disable2FA(disableCode);
      setTwoFactorEnabled(false);
      setShowDisable2FA(false);
      setDisableCode('');
      showMessage('success', t('auth.twoFactorDisabled'));
    } catch (error: any) {
      showMessage('error', error.response?.data?.message || t('auth.twoFactorInvalid'));
      setDisableCode('');
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const copySecret = () => {
    if (setupData?.secret) {
      navigator.clipboard.writeText(setupData.secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      await profileService.updateProfile(profileForm);
      showMessage('success', t('profile.updateSuccess'));
      loadProfile();
    } catch (error: any) {
      showMessage('error', error.response?.data?.message || t('profile.updateError'));
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      showMessage('error', t('profile.passwordMismatch'));
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      showMessage('error', t('profile.passwordMinLength'));
      return;
    }

    try {
      setSaving(true);
      await profileService.changePassword(passwordForm);
      showMessage('success', t('profile.passwordChangeSuccess'));
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error: any) {
      showMessage('error', error.response?.data?.message || t('profile.passwordChangeError'));
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('tr-TR');
  };

  const getInitials = () => {
    if (profile?.firstName && profile?.lastName) {
      return `${profile.firstName[0]}${profile.lastName[0]}`.toUpperCase();
    }
    return profile?.username?.substring(0, 2).toUpperCase() || 'U';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">{t('profile.title')}</h1>
          <p className="text-slate-500 text-sm">{t('profile.subtitle')}</p>
        </div>
      </div>

      {message && (
        <div className={`p-4 rounded-lg flex items-center gap-3 ${message.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
          {message.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
          <span className="text-sm font-medium">{message.text}</span>
          <button onClick={() => setMessage(null)} className="ml-auto text-current opacity-70 hover:opacity-100">&times;</button>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        {/* Profile Header */}
        <div className="h-32 bg-gradient-to-r from-blue-600 to-indigo-700 relative">
          <div className="absolute -bottom-12 left-8">
            <div className="w-24 h-24 rounded-2xl bg-white p-1.5 shadow-xl relative group">
              <div className="w-full h-full rounded-xl bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-2xl border-2 border-blue-50">
                {getInitials()}
              </div>
            </div>
          </div>
        </div>

        <div className="pt-16 pb-6 px-8 flex flex-col md:flex-row justify-between items-start gap-4">
          <div>
            <h2 className="text-2xl font-bold text-slate-800">{profile?.fullName || profile?.username}</h2>
            <div className="flex items-center gap-2 text-slate-500 mt-1">
              <span className="text-sm font-medium px-2 py-0.5 bg-blue-50 text-blue-700 rounded-md border border-blue-100">
                {profile?.roleNames || t('profile.user')}
              </span>
              {profile?.departmentName && (
                <>
                  <span className="text-xs">•</span>
                  <span className="text-sm">{profile.departmentName}</span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-8 border-b border-slate-100 flex gap-8">
          <button
            onClick={() => setActiveTab('info')}
            className={`pb-4 text-sm font-bold transition-all border-b-2 flex items-center gap-2 ${activeTab === 'info' ? 'text-blue-600 border-blue-600' : 'text-slate-400 border-transparent hover:text-slate-600'}`}
          >
            <User size={16} /> {t('profile.personalInfo')}
          </button>
          <button
            onClick={() => setActiveTab('security')}
            className={`pb-4 text-sm font-bold transition-all border-b-2 flex items-center gap-2 ${activeTab === 'security' ? 'text-blue-600 border-blue-600' : 'text-slate-400 border-transparent hover:text-slate-600'}`}
          >
            <Shield size={16} /> {t('profile.changePassword')}
          </button>
          <button
            onClick={() => setActiveTab('activity')}
            className={`pb-4 text-sm font-bold transition-all border-b-2 flex items-center gap-2 ${activeTab === 'activity' ? 'text-blue-600 border-blue-600' : 'text-slate-400 border-transparent hover:text-slate-600'}`}
          >
            <Activity size={16} /> {t('profile.activities')}
          </button>
        </div>

        <div className="p-8">
          {/* Kisisel Bilgiler Tab */}
          {activeTab === 'info' && (
            <form onSubmit={handleProfileUpdate} className="animate-in fade-in duration-300">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">{t('employees.firstName')}</label>
                  <input
                    type="text"
                    value={profileForm.firstName}
                    onChange={(e) => setProfileForm({ ...profileForm, firstName: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">{t('employees.lastName')}</label>
                  <input
                    type="text"
                    value={profileForm.lastName}
                    onChange={(e) => setProfileForm({ ...profileForm, lastName: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">{t('common.email')}</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <input
                      type="email"
                      value={profileForm.email}
                      onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                      className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">{t('common.phone')}</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <input
                      type="tel"
                      value={profileForm.phone || ''}
                      onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                      className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm"
                      placeholder="+90 5XX XXX XX XX"
                    />
                  </div>
                </div>
              </div>

              {/* Salt okunur bilgiler */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6 pt-6 border-t border-slate-100">
                <div className="flex items-center gap-4 p-4 rounded-xl border border-slate-100 bg-slate-50/50">
                  <div className="p-2.5 bg-white rounded-lg text-blue-600 shadow-sm">
                    <User size={20} />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">{t('auth.username')}</p>
                    <p className="text-sm font-medium text-slate-700">{profile?.username}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-4 rounded-xl border border-slate-100 bg-slate-50/50">
                  <div className="p-2.5 bg-white rounded-lg text-blue-600 shadow-sm">
                    <Calendar size={20} />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">{t('profile.lastLogin')}</p>
                    <p className="text-sm font-medium text-slate-700">{formatDate(profile?.lastLoginDate)}</p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={saving}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all font-bold text-sm shadow-md active:scale-95 disabled:opacity-70 flex items-center gap-2"
                >
                  {saving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-b-transparent rounded-full animate-spin"></div>
                      {t('profile.saving')}
                    </>
                  ) : (
                    <>
                      <Save size={16} /> {t('common.save')}
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-300 space-y-8">
              {/* 2FA Management Section */}
              <div className="max-w-lg">
                <h3 className="text-base font-bold text-slate-800 mb-4 flex items-center gap-2">
                  <Shield size={18} className="text-blue-600" />
                  {t('auth.twoFactorTitle')}
                </h3>

                {/* 2FA Status Card */}
                <div className={`p-5 rounded-xl border-2 transition-all ${twoFactorEnabled ? 'bg-green-50/50 border-green-200' : 'bg-amber-50/50 border-amber-200'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2.5 rounded-lg ${twoFactorEnabled ? 'bg-green-100 text-green-600' : 'bg-amber-100 text-amber-600'}`}>
                        {twoFactorEnabled ? <ShieldCheck size={22} /> : <ShieldOff size={22} />}
                      </div>
                      <div>
                        <p className={`text-sm font-bold ${twoFactorEnabled ? 'text-green-800' : 'text-amber-800'}`}>
                          {twoFactorEnabled ? t('auth.twoFactorEnabled') : t('auth.twoFactorDisabled')}
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {twoFactorEnabled ? t('auth.twoFactorPrompt') : t('auth.twoFactorRequired')}
                        </p>
                      </div>
                    </div>
                    {twoFactorEnabled ? (
                      <button
                        type="button"
                        onClick={() => { setShowDisable2FA(true); setDisableCode(''); setMessage(null); }}
                        disabled={twoFactorLoading}
                        className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-all font-bold text-xs disabled:opacity-70"
                      >
                        {t('auth.disable2fa')}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={handleStart2FASetup}
                        disabled={twoFactorLoading}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all font-bold text-xs shadow-md disabled:opacity-70 flex items-center gap-2"
                      >
                        {twoFactorLoading ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <>
                            <Smartphone size={14} />
                            {t('auth.enable2fa')}
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>

                {/* 2FA Setup Flow (Enable) */}
                {showSetup2FA && setupData && (
                  <div className="mt-5 p-5 bg-white border border-slate-200 rounded-xl shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                        <QrCode size={16} className="text-blue-600" />
                        {t('auth.twoFactorSetup')}
                      </h4>
                      <button
                        type="button"
                        onClick={() => { setShowSetup2FA(false); setSetupData(null); setSetupCode(''); }}
                        className="p-1 text-slate-400 hover:text-slate-600 rounded transition-colors"
                      >
                        <X size={16} />
                      </button>
                    </div>

                    <form onSubmit={handleEnable2FA} className="space-y-4">
                      {/* QR Code */}
                      <div className="flex flex-col items-center">
                        <p className="text-xs text-slate-500 mb-3">{t('auth.scanQrCode')}</p>
                        <div className="p-3 bg-white border-2 border-slate-100 rounded-xl shadow-inner">
                          <img
                            src={setupData.qrCodeUrl}
                            alt="2FA QR Code"
                            className="w-44 h-44"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(setupData.otpauthUrl)}`;
                            }}
                          />
                        </div>
                      </div>

                      {/* Manual secret */}
                      <div>
                        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">{t('auth.manualEntry')}</p>
                        <div className="flex items-center gap-2">
                          <code className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-mono text-slate-700 select-all break-all">
                            {setupData.secret}
                          </code>
                          <button
                            type="button"
                            onClick={copySecret}
                            className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                          >
                            {copied ? <CheckCircle size={16} className="text-green-500" /> : <Copy size={16} />}
                          </button>
                        </div>
                      </div>

                      {/* Verification code */}
                      <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                          {t('auth.enterVerificationCode')}
                        </label>
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          maxLength={6}
                          value={setupCode}
                          onChange={(e) => setSetupCode(e.target.value.replace(/\D/g, '').substring(0, 6))}
                          className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none text-xl text-center font-mono tracking-[0.5em] transition-all"
                          placeholder="000000"
                          autoFocus
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={twoFactorLoading || setupCode.length !== 6}
                        className="w-full py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all font-bold text-sm shadow-md disabled:opacity-70 flex items-center justify-center gap-2"
                      >
                        {twoFactorLoading ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <>
                            <ShieldCheck size={16} />
                            {t('auth.enable2fa')}
                          </>
                        )}
                      </button>
                    </form>
                  </div>
                )}

                {/* 2FA Disable Dialog */}
                {showDisable2FA && (
                  <div className="mt-5 p-5 bg-white border border-red-200 rounded-xl shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-sm font-bold text-red-700 flex items-center gap-2">
                        <ShieldOff size={16} />
                        {t('auth.disable2fa')}
                      </h4>
                      <button
                        type="button"
                        onClick={() => { setShowDisable2FA(false); setDisableCode(''); }}
                        className="p-1 text-slate-400 hover:text-slate-600 rounded transition-colors"
                      >
                        <X size={16} />
                      </button>
                    </div>

                    <form onSubmit={handleDisable2FA} className="space-y-4">
                      <p className="text-xs text-slate-500">{t('auth.enterVerificationCode')}</p>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={6}
                        value={disableCode}
                        onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').substring(0, 6))}
                        className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 focus:outline-none text-xl text-center font-mono tracking-[0.5em] transition-all"
                        placeholder="000000"
                        autoFocus
                      />
                      <button
                        type="submit"
                        disabled={twoFactorLoading || disableCode.length !== 6}
                        className="w-full py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all font-bold text-sm shadow-md disabled:opacity-70 flex items-center justify-center gap-2"
                      >
                        {twoFactorLoading ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <>
                            <ShieldOff size={16} />
                            {t('auth.disable2fa')}
                          </>
                        )}
                      </button>
                    </form>
                  </div>
                )}
              </div>

              {/* Divider */}
              <div className="border-t border-slate-200" />

              {/* Password Change Section */}
              <div className="max-w-md">
                <h3 className="text-base font-bold text-slate-800 mb-4 flex items-center gap-2">
                  <Key size={18} className="text-blue-600" />
                  {t('profile.changePassword')}
                </h3>

                <form onSubmit={handlePasswordUpdate} className="space-y-6">
                  <div className="bg-blue-50 border border-blue-100 p-4 rounded-xl flex gap-3">
                    <ShieldCheck className="text-blue-600 shrink-0" size={20} />
                    <p className="text-xs text-blue-700 leading-relaxed">
                      {t('profile.passwordWarning')}
                    </p>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">{t('profile.currentPassword')}</label>
                    <div className="relative">
                      <Key className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                      <input
                        type={showPassword ? "text" : "password"}
                        value={passwordForm.currentPassword}
                        onChange={(e) => setPasswordForm({...passwordForm, currentPassword: e.target.value})}
                        required
                        className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm transition-all"
                        placeholder="********"
                      />
                    </div>
                  </div>

                  <div className="pt-4 border-t border-slate-100">
                    <div className="space-y-4">
                      <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">{t('profile.newPassword')}</label>
                        <div className="relative">
                          <Key className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                          <input
                            type={showPassword ? "text" : "password"}
                            value={passwordForm.newPassword}
                            onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                            required
                            minLength={6}
                            className="w-full pl-10 pr-12 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm transition-all"
                            placeholder={t('profile.minChars')}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                          >
                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">{t('profile.confirmNewPassword')}</label>
                        <div className="relative">
                          <Key className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                          <input
                            type={showPassword ? "text" : "password"}
                            value={passwordForm.confirmPassword}
                            onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                            required
                            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm transition-all"
                            placeholder={t('profile.reenterPassword')}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end pt-4">
                    <button
                      type="submit"
                      disabled={saving}
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all font-bold text-sm shadow-md active:scale-95 disabled:opacity-70 flex items-center gap-2"
                    >
                      {saving ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-b-transparent rounded-full animate-spin"></div>
                          {t('profile.changing')}
                        </>
                      ) : (
                        <>
                          <Save size={16} /> {t('profile.updatePassword')}
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Aktiviteler Tab */}
          {activeTab === 'activity' && (
            <div className="animate-in fade-in duration-300">
              <div className="space-y-3">
                {activities.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Activity size={48} className="mx-auto mb-4 opacity-30" />
                    <p>{t('profile.noActivities')}</p>
                  </div>
                ) : (
                  activities.map((activity) => (
                    <div key={activity.id} className="flex items-center gap-4 p-4 rounded-xl border border-slate-100 bg-slate-50/50">
                      <div className="p-2.5 bg-white rounded-lg text-blue-600 shadow-sm">
                        <Activity size={20} />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-slate-700">{activity.description}</p>
                        <p className="text-xs text-slate-400 mt-0.5">{formatDate(activity.activityDate)}</p>
                      </div>
                      {activity.ipAddress && (
                        <span className="text-xs text-slate-400 bg-slate-100 px-2 py-1 rounded">
                          {activity.ipAddress}
                        </span>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;
